// Maximum file size (4MB)
const MAX_FILE_SIZE = 4 * 1024 * 1024;

// Allowed image types
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];

interface UploadImageOptions {
  file: File;
  studioId: string;
  onProgress?: (progress: number) => void;
}

/**
 * Validates an image file
 * @param file The file to validate
 * @returns Object containing validation result and error message if any
 */
export function validateImage(file: File): { valid: boolean; error?: string } {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File size exceeds maximum allowed size of 4MB`,
    };
  }

  // Check MIME type
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `File type ${file.type} is not allowed. Allowed types: ${ALLOWED_IMAGE_TYPES.join(", ")}`,
    };
  }

  return { valid: true };
}

/**
 * Uploads an image file using the /api/images/upload endpoint
 * @param options Upload options including the file, studioId and optional progress callback
 * @returns Promise resolving to the uploaded file URL
 */
export async function uploadImage({
  file,
  studioId,
  onProgress,
}: UploadImageOptions): Promise<string> {
  // Validate the file
  const validation = validateImage(file);

  if (!validation.valid) {
    throw new Error(validation.error);
  }

  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("studioId", studioId);

    const response = await fetch("/api/images/upload", {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error);
    }

    const { url } = await response.json();
    return url;
  } catch (error) {
    console.error("Error uploading image:", error);
    throw new Error("Failed to upload image");
  }
}
